'use client';
import { Button, type ButtonProps } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2Icon } from 'lucide-react';
import { ReactNode, useState } from 'react';
import { createRoot, Root } from 'react-dom/client';

export type ModalDirectiveConfig = {
  title: ReactNode;
  content: ReactNode;
  onOpenChange?: (isOpen: boolean) => void;
  onOk?: () => Promise<void>;
  onCancel?: () => Promise<void>;
  okText?: string;
  cancelText?: string;
  showCancel?: boolean;
  okVariant?: ButtonProps['variant'];
  tone?: 'default' | 'danger';
};

function InfoModal({
  onOpenChange,
  title,
  content,
  onOk,
  okText,
  cancelText,
  onCancel,
  showCancel,
  okVariant = 'default',
}: ModalDirectiveConfig) {
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    onOpenChange?.(false);
  };

  return (
    <Dialog
      open={true}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <DialogContent
        onInteractOutside={(e) => e.preventDefault()}
        className="gap-0 rounded-2xl border-border/70 p-0 shadow-xl sm:max-w-130 [&>button]:hidden"
      >
        <DialogHeader className="gap-2 px-6 pt-6 text-left sm:px-7 sm:pt-7">
          <DialogTitle className="text-xl font-semibold tracking-tight text-foreground">
            {title}
          </DialogTitle>
        </DialogHeader>
        <div className="px-6 pb-6 pt-3 sm:px-7">
          <DialogDescription className="text-[15px] leading-7 text-foreground/78">
            {content}
          </DialogDescription>
        </div>
        <DialogFooter className="px-6 py-4 sm:px-7">
          {showCancel && (
            <Button
              variant="outline"
              className="min-w-22"
              onClick={() => {
                if (onCancel) {
                  onCancel();
                }
                handleClose();
              }}
            >
              {cancelText || '取消'}
            </Button>
          )}
          <Button
            variant={okVariant}
            className="min-w-22"
            disabled={loading}
            onClick={async () => {
              if (!onOk) {
                handleClose();
                return;
              }

              setLoading(true);
              try {
                await onOk();
                handleClose();
              } catch (e) {
                console.error(e);
              }
              setLoading(false);
            }}
          >
            {loading && <Loader2Icon className="h-4 w-4 animate-spin" />}
            {okText || '确定'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

class ModalDirective {
  private static instance: null | ModalDirective = null;

  private options: ModalDirectiveConfig = {
    title: '',
    content: '',
  };

  private root: Root | undefined;

  static getInstance() {
    if (!ModalDirective.instance) {
      ModalDirective.instance = new ModalDirective();
    }
    return ModalDirective.instance;
  }

  open(config: ModalDirectiveConfig) {
    this.options = { ...this.options, ...config };
    this.createRoot();
    this.render();
  }

  close() {
    this.root?.unmount();
    this.root = undefined;
    const modals = document.getElementsByClassName('choose-material-modal');
    for (let i = 0; i < modals.length; i++) {
      modals[i].remove();
    }
  }

  createRoot() {
    if (!this.root) {
      const div = document.createElement('div');
      div.classList.add('choose-material-modal');
      document.body.appendChild(div);
      this.root = createRoot(div);
    }
  }

  render() {
    const { options } = this;
    const configOnOpenChange = options.onOpenChange;
    this.root?.render(
      <InfoModal
        {...options}
        onOpenChange={(isOpen) => {
          configOnOpenChange?.(isOpen);
          this.close();
        }}
      />,
    );
  }
}

export default ModalDirective.getInstance();
